
import { updateProfile } from '@/api';
import Avatar from '@/components/UI/avatar/Avatar.vue';
import BackButton from '@/components/UI/backButton/BackButton.vue';
import Button from '@/components/UI/button/Button.vue';
import Notification from '@/components/UI/Notification.vue';
import { IUserUpdate, User } from '@/models/main';
import { format, utcToZonedTime } from 'date-fns-tz';
import { Component, Vue } from 'vue-property-decorator';
import { mapState } from 'vuex';
import { notificationErrorText } from '@/utils/PortalConstants';
import { AuthData } from '@/models/auth';

@Component({
  computed: {
    ...mapState('moduleUser', { user: 'user' }),
    ...mapState('moduleAuthData', ['authData']),
  },
  components: {
    Avatar,
    BackButton,
    Button,
    Notification,
  },
})
export default class Profile extends Vue {
  private notification = false;
  private notificationColor = '';
  private notificationText = '';

  private user!: User;
  public authData!: AuthData;

  private loadingUpdate = false;
  private selectZone: string | undefined = '';
  private selectedFile: File | '' = '';
  private newAvatar: string | ArrayBuffer | null = '';
  private intervalId: number;

  private date = new Date();
  private isSelecting = false;

  private format = format;
  private utcToZonedTime = utcToZonedTime;

  private timeZoneItems = [
    {
      timeZone: '+08:00',
      zoneCode: 'AWST',
      city: 'Perth',
    },
    {
      timeZone: '+08:45',
      zoneCode: 'ACWST',
      city: 'Eucla',
    },
    {
      timeZone: '+09:30',
      zoneCode: 'ACST',
      city: 'Darwin',
    },
    {
      timeZone: '+10:00',
      zoneCode: 'AEST',
      city: 'Brisbane',
    },
    {
      timeZone: '+10:30',
      zoneCode: 'ACDT',
      city: 'Adelaide',
    },
    {
      timeZone: '+11:00',
      zoneCode: 'AEDT',
      city: 'Sydney',
    },
    {
      timeZone: '+11:00',
      zoneCode: 'LHDT',
      city: 'Lord Howe Island',
    },
  ];
  private get buttonText() {
    return this.selectedFile ? this.selectedFile.name : '';
  }

  /* taken from
   * https://codepen.io/blachocolat/pen/BgMKRQ
   */
  private onButtonClick() {
    this.isSelecting = true;
    window.addEventListener(
      'focus',
      () => {
        this.isSelecting = false;
      },
      { once: true },
    );
    (this.$refs.uploader as HTMLInputElement).value = '';
    (this.$refs.uploader as HTMLInputElement).click();
  }

  private async onFileChanged(e) {
    if (e.target.files[0].size > 2 * 1024 * 1024) {
      this.notification = true;
      this.notificationColor = 'red';
      this.notificationText = 'Max size 2mb';
      (this.$refs.uploader as HTMLInputElement).value = '';
      return;
    }
    this.selectedFile = e.target.files[0];

    if (this.selectedFile) {
      const fr = new FileReader();
      fr.onload = () => {
        this.newAvatar = fr.result;
      };
      fr.readAsDataURL(e.target.files[0]);
    }
  }

  // update profile
  private clickUpdate() {
    this.loadingUpdate = true;

    const params: IUserUpdate = {};
    if (this.user.timezone !== `Australia/${this.selectZone}`) {
      params.timezone = `Australia/${this.selectZone}`;
    }
    const formData = new FormData();
    formData.append('payload', JSON.stringify(params));
    formData.append('avatar_file', this.selectedFile);

    const form = this.$refs.form as Vue & { validate: () => boolean };
    if (form.validate() && (params.timezone !== this.user.timezone || !!this.selectedFile)) {
      updateProfile(formData)
        .then(res => {
          if (res.status === 200) {
            this.$store.dispatch('moduleUser/setUser', res.data);
            this.selectedFile = '';
          }
        })
        .catch(() => {
          this.$notify({
            group: 'network',
            title: 'Error updating the profile',
            text: notificationErrorText,
            type: 'warn',
          });
        })
        .finally(() => {
          this.loadingUpdate = false;
          this.$notify({
            group: 'network',
            text: 'Profile updated',
            type: 'success',
            duration: 3000,
          });
        });
    }
  }
  private clickReset() {
    this.selectZone = this.user.timezone?.split('/')[1];
    this.selectedFile = '';
    this.newAvatar = '';
  }

  private created() {
    this.intervalId = setInterval(() => (this.date = new Date()), 1000) as unknown as number;
  }

  private beforeDestroy() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
  }
  private mounted() {
    this.selectZone = this.user.timezone?.split('/')[1];
  }
}
